import type { CityConfig } from "@/pages/city-template/types";

export const greenBayConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Green Bay",
  state: "Wisconsin",
  stateAbbr: "WI",
  slug: "green-bay",
  route: "/cities/green-bay",
  title: "Emergency Passport Green Bay, WI — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Green Bay, WI — as fast as 24 hours. No Wisconsin passport agency. Fast Passport Center couriers to Chicago. A+ BBB rated. Serving Brown County and northeast Wisconsin.",
  h1: "Emergency & Expedited Passport Services in Green Bay, WI",
  heroSubtitle: "Green Bay and northeast Wisconsin have no local passport agency — the nearest is Chicago, about 200 miles south. Fast Passport Center's registered couriers deliver your documents directly to the Chicago Passport Agency, getting you a new or renewed passport as fast as 24–48 hours.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Green%20Bay%20Wisconsin%20Fox%20River%20downtown%20waterfront%20at%20sunset%20with%20modern%20buildings%20and%20bridges%2C%20warm%20amber%20and%20teal%20tones%20with%20Fox%20River%20reflecting%20city%20lights%2C%20autumn%20foliage%20along%20the%20waterfront%2C%20professional%20cityscape%20photography%2C%20wide%20angle%20composition%20with%20clear%20sky%2C%20no%20text%20or%20people%20visible&width=1800&height=700&seq=greenbay-hero-001&orientation=landscape",
  heroImageAlt: "Green Bay Wisconsin Fox River downtown waterfront at sunset",
  geoPosition: "44.5133;-88.0133",
  geoPlacename: "Green Bay, Wisconsin",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "230 S Dearborn St, Suite 380, Chicago, IL 60604",
    distance: "Approximately 200 miles from Green Bay — about 3.5 hours via I-43 south",
  },
  nearbyCitiesZips: [
    { name: "Downtown Green Bay", zips: "54301, 54302" },
    { name: "De Pere", zips: "54115" },
    { name: "Ashwaubenon", zips: "54304" },
    { name: "Howard", zips: "54313" },
    { name: "Allouez", zips: "54301" },
    { name: "Bellevue", zips: "54311" },
    { name: "Suamico", zips: "54173" },
    { name: "Pulaski", zips: "54162" },
    { name: "Marinette", zips: "54143" },
    { name: "Oconto", zips: "54153" },
    { name: "Kaukauna", zips: "54130" },
    { name: "Appleton", zips: "54911, 54914" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier to Chicago Passport Agency — no 7-hour roundtrip required" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your northeast Wisconsin case" },
    { icon: "ri-map-pin-2-line", title: "Remote Northeast WI", desc: "Serving Appleton, Marinette, Oconto, and Fox Valley communities" },
  ],
  internalLinks: [
    { name: "Emergency passport in Milwaukee WI", route: "/milwaukee" },
    { name: "Expedited passport in Madison WI", route: "/madison-wi" },
    { name: "Urgent passport in Racine WI", route: "/racine" },
    { name: "Wisconsin passport hub", route: "/wisconsin" },
    { name: "Chicago passport services", route: "/illinois" },
    { name: "Same-day passport in Minneapolis MN", route: "/minneapolis" },
    { name: "Emergency passport in Indianapolis IN", route: "/indianapolis" },
  ],
  fpcOfficeAddress: "230 S Dearborn St, Suite 380, Chicago, IL 60604",
  fpcOfficeNote: "Fast Passport Center serves Green Bay remotely via secure courier. Our registered couriers hand-deliver your documents directly to the Chicago Passport Agency — about 200 miles south. This means Green Bay travelers get 24–48 hour turnaround without a 7-hour roundtrip drive.",
  fpcOfficeDistance: "Serving all of Brown County and northeast Wisconsin",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Green Bay and northeast Wisconsin. From downtown Green Bay and De Pere to Appleton, the Fox Valley, and the Door Peninsula — we help northeast Wisconsin travelers get their passports fast through our Chicago courier service.",
  nearbyCitiesIntro: "We serve Green Bay and surrounding northeast Wisconsin communities including De Pere, Ashwaubenon, Howard, Appleton, Kaukauna, Marinette, Oconto, and the Door County Peninsula.",
  howItWorksIntro: "Most Green Bay travelers complete everything online and ship documents overnight. No need to drive 3.5 hours each way to Chicago. Our process is designed for northeast Wisconsin residents who need fast, reliable results.",
  mistakesIntro: "Green Bay travelers — including Packers staff, healthcare workers, and Fox Valley professionals — have specific passport needs. Here are the most common mistakes we see.",
  testimonialsIntro: "Green Bay travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Green Bay? Here are the most common ones we hear from Brown County travelers.",
  statePageRoute: "/states/wisconsin",
  statePageName: "Wisconsin",
  dmvWebsite: "https://www.dot.wisconsin.gov/drivers",
  dmvLabel: "Wisconsin DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Chicago Agency courier — no long drive",
        "Predictable turnaround for Green Bay travelers",
        "Ideal for GRB Airport departures",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned travel from GRB or ORD",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "No document review — errors cause rejection",
        "In-person clerk appointment required",
        "Workload shifts with government shutdowns",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "3.5-hour drive each way to Chicago",
        "7-hour roundtrip for Green Bay travelers",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Green Bay travelers with flights within 72 hours — our couriers hand-deliver to Chicago Passport Agency, as fast as 24 hours.",
      bullets: ["24–48 hour turnaround available", "No 7-hour roundtrip to Chicago", "Chicago Agency courier delivery"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Green Bay adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit for most renewals", "Mail-in or courier options", "Serving all of northeast Wisconsin"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support across Brown County.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement for Green Bay and northeast Wisconsin residents with DS-64 form guidance.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support across northeast Wisconsin.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport name change after marriage, divorce, or legal changes for Green Bay and Fox Valley residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Green Bay WI", description: "Urgent passport processing for Green Bay travelers via Chicago Passport Agency courier — as fast as 24 hours." },
    { name: "Passport Renewal Green Bay WI", description: "Fast passport renewal for Green Bay and Brown County, Wisconsin residents." },
    { name: "New Passport Green Bay WI", description: "First-time passport application assistance for Green Bay and northeast Wisconsin residents." },
    { name: "Lost Passport Green Bay WI", description: "Passport replacement for lost or stolen passports for Green Bay residents." },
    { name: "Child Passport Green Bay WI", description: "Minor passport services for Green Bay and northeast Wisconsin families." },
    { name: "Name Change Passport Green Bay WI", description: "Passport name change service for Green Bay, WI residents." },
  ],
  testimonials: [
    {
      name: "Tom H.",
      location: "Ashwaubenon, WI",
      rating: 5,
      text: "Work trip to Germany, passport expired, found out 5 days before departure. Fast Passport Center sent a courier to Chicago and I had a new passport in 48 hours. No way I was driving 7 hours round trip.",
      service: "Emergency Passport",
    },
    {
      name: "Amanda P.",
      location: "De Pere, WI",
      rating: 5,
      text: "Renewed our whole family's passports before a cruise. Fast Passport Center handled all four and we had them back in 6 days. Highly recommend for northeast Wisconsin families.",
      service: "Expedited Renewal",
    },
    {
      name: "Ryan K.",
      location: "Appleton, WI",
      rating: 5,
      text: "Lost my passport in Door County a week before a trip to Iceland. They walked me through the DS-64 and got me a replacement in record time. Saved the trip.",
      service: "Lost Passport",
    },
    {
      name: "Laura B.",
      location: "Howard, WI",
      rating: 5,
      text: "My son needed a passport for his first international trip. Fast Passport Center made the whole process easy — from the DS-11 form to finding an acceptance facility near Howard. Great service.",
      service: "First-Time Passport",
    },
  ],
  facilities: [
    {
      id: "grb-1",
      name: "Green Bay Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "411 E Walnut St",
      city: "Green Bay",
      state: "WI",
      zip: "54301",
      phone: "(920) 433-5060",
      hours: "Mon–Fri: 8:30 AM – 5:30 PM",
      distance: "Downtown Green Bay",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for downtown Green Bay. Appointment required. Photo services may not be available — bring photos.",
      icon: "ri-mail-line",
    },
    {
      id: "grb-2",
      name: "Brown County Register of Deeds",
      type: "County Office Passport Acceptance",
      address: "305 E Walnut St, Room 101",
      city: "Green Bay",
      state: "WI",
      zip: "54301",
      phone: "(920) 448-4470",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "Downtown Green Bay, courthouse complex",
      website: "https://www.browncountywi.gov/",
      websiteLabel: "Brown County",
      notes: "Accepts passport applications. Convenient for downtown Green Bay residents. No photo services on-site.",
      icon: "ri-government-line",
    },
    {
      id: "grb-3",
      name: "De Pere Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "401 Main Ave",
      city: "De Pere",
      state: "WI",
      zip: "54115",
      phone: "(920) 336-5650",
      hours: "Mon–Fri: 9:00 AM – 5:00 PM",
      distance: "8 miles south of downtown Green Bay",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for De Pere and southern Brown County residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "grb-4",
      name: "Appleton Downtown Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "200 N Drew St",
      city: "Appleton",
      state: "WI",
      zip: "54911",
      phone: "(920) 380-0750",
      hours: "Mon–Fri: 8:30 AM – 5:30 PM",
      distance: "32 miles south of Green Bay",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major Fox Valley acceptance facility. Convenient for Appleton, Kaukauna, and Fox Cities residents.",
      icon: "ri-mail-line",
    },
    {
      id: "grb-5",
      name: "Howard Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2900 Packer Dr",
      city: "Howard",
      state: "WI",
      zip: "54313",
      phone: "(920) 434-2960",
      hours: "Mon–Fri: 9:00 AM – 5:00 PM",
      distance: "5 miles northwest of downtown Green Bay",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Howard and Suamico residents. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "grb-6",
      name: "Marinette Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1800 Hall Ave",
      city: "Marinette",
      state: "WI",
      zip: "54143",
      phone: "(715) 735-5150",
      hours: "Mon–Fri: 9:00 AM – 5:00 PM",
      distance: "50 miles north of Green Bay",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Marinette and Menominee county residents in the northernmost part of the service area.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — call NPIC, limited slots in Chicago", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Required — 200 mile drive to Chicago via I-43", fpc: "Most Green Bay travelers ship documents overnight" },
    { feature: "Processing Speed", agency: "Life-or-death emergencies only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection and delay", fpc: "Professional review to reduce processing delays" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission availability as registered courier" },
    { feature: "Predictability", agency: "Appointments can be cancelled — no guarantee", fpc: "Predictable processing based on selected speed option" },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel only", fpc: "Most U.S. citizens with required documentation" },
    { feature: "Cost vs Resellers", agency: "Government fees only — but you handle logistics", fpc: "Best price guarantee — 30-100% less than FedEx, Staples & resellers" },
    { feature: "Green Bay-Specific Guidance", agency: "Generic — no Packers, paper industry, or Fox Valley expertise", fpc: "Full Green Bay/Chicago courier/northeast Wisconsin knowledge built in" },
  ],
  mistakes: [
    {
      icon: "ri-car-line",
      title: "Planning to Drive to Chicago Without Appointment",
      description: "At 200 miles away, a wasted trip to Chicago is a 7-hour roundtrip. The Chicago Passport Agency requires appointments and turns away walk-ins. Fast Passport Center eliminates this problem entirely.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Photo Background or Size",
      description: "Passport photos must be 2×2 inches with a white background. Verify at any Green Bay CVS or Walgreens before submitting.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "Booking GRB to Chicago Connections Without a Valid Passport",
      description: "Many Green Bay travelers connect through Chicago for international flights. Ensure your passport is valid before booking — airlines cannot board you without one.",
    },
    {
      icon: "ri-file-list-line",
      title: "Using the Wrong Passport Form",
      description: "Renewals use DS-82, first-time applications and lost passports use DS-11/DS-64. Using the wrong form delays your application by weeks.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Both Parents for Child Passport",
      description: "For minors under 16, both parents must appear or one must provide notarized consent on DS-3053. Brown County families often miss this step.",
    },
    {
      icon: "ri-time-line",
      title: "Starting Too Late Before Travel",
      description: "Standard processing takes 8–11 weeks. Green Bay travelers planning summer travel to Europe or cruises often start too late. Begin your expedited application at least 6 weeks in advance.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes from anywhere in Green Bay." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop your documents at any UPS location in Green Bay or the Fox Valley." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the Chicago Passport Agency." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the Chicago Passport Agency using our pre-secured courier submission slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Green Bay address — fully trackable every step." },
  ],
  faqs: [
    {
      q: "Does Green Bay or Wisconsin have a local passport agency?",
      a: "No. Wisconsin has no regional passport agency. The nearest option is the Chicago Passport Agency at 230 S Dearborn St, Chicago, IL — approximately 200 miles from Green Bay (3.5 hours by car, 7-hour roundtrip). Fast Passport Center's registered couriers deliver directly to Chicago so Green Bay travelers don't need to make the drive.",
    },
    {
      q: "How fast can I get an emergency passport in Green Bay?",
      a: "Through Fast Passport Center's courier service to the Chicago Passport Agency, Green Bay travelers can receive a passport in as little as 24–48 hours. For standard expedited processing, we deliver in 2–9 business days. Standard USPS mail takes 8–11 weeks.",
    },
    {
      q: "Is it worth driving to Chicago for a passport from Green Bay?",
      a: "For most Green Bay travelers, the 200-mile, 7-hour roundtrip to Chicago is not worth it. The Chicago Passport Agency requires pre-scheduled appointments and turns away walk-ins. Fast Passport Center handles everything remotely — your documents ship overnight and our couriers handle the Chicago submission.",
    },
    {
      q: "What acceptance facilities are near Green Bay?",
      a: "Green Bay Main Post Office (411 E Walnut St), Brown County Register of Deeds (305 E Walnut St), De Pere Post Office, and Howard Post Office all accept passport applications. Appleton Post Office serves Fox Valley residents. All require appointments.",
    },
    {
      q: "Can I fly internationally from Green Bay Austin Straubel Airport?",
      a: "Green Bay Austin Straubel Airport (GRB) has domestic service only. International travelers from Green Bay typically connect through Chicago O'Hare (ORD), Milwaukee (MKE), or Minneapolis (MSP). A valid U.S. passport is required for all international connections.",
    },
    {
      q: "Does the Door County area have passport acceptance facilities?",
      a: "Door County has limited passport acceptance facilities. We recommend using the Green Bay Main Post Office or Brown County Register of Deeds for Door County residents. Fast Passport Center can handle renewals entirely by mail, eliminating the need to travel to Green Bay for eligible applicants.",
    },
    {
      q: "What documents do I need for a first-time passport in Green Bay?",
      a: "You'll need Form DS-11, a certified Wisconsin birth certificate, a valid photo ID, two passport photos, and fee payment. First-time applicants must appear in person at an acceptance facility. We provide a checklist and help schedule your Green Bay area appointment.",
    },
    {
      q: "How do I renew my passport in Green Bay?",
      a: "If your most recent passport was issued when you were 16 or older, within the last 15 years, and in your current name, you can renew by mail with Form DS-82. Fast Passport Center's courier service to Chicago achieves 2–9 business day turnaround — far faster than standard USPS processing.",
    },
    {
      q: "Can I get a passport for a Canada fishing trip from Green Bay?",
      a: "Land and sea crossings to Canada may use a REAL ID, passport card, or passport book. Air travel to Canada requires a passport book. For any international fishing trip or hunting trip to Canada, having a full passport book is strongly recommended for the smoothest possible crossing.",
    },
    {
      q: "How does Fast Passport Center serve northeast Wisconsin communities?",
      a: "We serve all of northeast Wisconsin remotely — including Green Bay, Appleton, the Fox Valley, Marinette, Oconto, Sheboygan, and Door County. Applicants ship their documents via overnight courier from any UPS location in the region. Our agents review documents and courier them to the Chicago Passport Agency.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — W Mason St", address: "2145 W Mason St, Green Bay, WI 54303", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/" },
    { name: "Walgreens — E Mason St", address: "920 E Mason St, Green Bay, WI 54301", hours: "Mon–Sun: 7AM–10PM", website: "https://www.walgreens.com/" },
    { name: "FedEx Office — Oneida St", address: "2601 S Oneida St, Green Bay, WI 54304", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM", website: "https://www.fedex.com/" },
    { name: "UPS Store — Oneida St", address: "2700 S Oneida St, Green Bay, WI 54304", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM", website: "https://www.theupsstore.com/" },
    { name: "Walgreens — Appleton", address: "2621 E College Ave, Appleton, WI 54915", hours: "Mon–Sun: 7AM–10PM", website: "https://www.walgreens.com/" },
    { name: "Walmart — Oneida St", address: "1825 S Oneida St, Green Bay, WI 54304", hours: "Mon–Sun: 6AM–11PM", website: "https://www.walmart.com/" },
  ],
  dmvLocations: [
    { name: "WI DMV — Green Bay North", address: "2750 Ramada Way, Green Bay, WI 54304", hours: "Mon–Fri: 8:30AM–4:30PM", note: "REAL ID and driver's license renewals for Brown County residents.", featured: true },
    { name: "WI DMV — Appleton", address: "3600 E Calumet St, Appleton, WI 54915", hours: "Mon–Fri: 8:30AM–4:30PM", note: "Serves Fox Valley and Outagamie County residents.", featured: false },
    { name: "WI DMV — De Pere", address: "500 Commerce Dr, De Pere, WI 54115", hours: "Mon–Fri: 8:30AM–4:30PM", note: "Convenient for southern Brown County residents.", featured: false },
  ],
  airports: [
    {
      code: "GRB",
      name: "Green Bay Austin Straubel International Airport",
      distance: "6 miles southwest of downtown Green Bay",
      address: "2077 Airport Dr, Green Bay, WI 54313",
      note: "Green Bay's regional airport with domestic service. International travelers connect through Chicago, Milwaukee, or Minneapolis.",
      flightUrl: "https://www.flygrb.com/",
      flightLabel: "GRB Airport",
      featured: true,
    },
    {
      code: "ORD",
      name: "Chicago O'Hare International Airport",
      distance: "200 miles south of Green Bay",
      address: "10000 W O'Hare Ave, Chicago, IL 60666",
      note: "Major international hub used by most Green Bay travelers for overseas flights. 3.5-hour drive from Green Bay.",
      flightUrl: "https://www.flychicago.com/ohare/",
      flightLabel: "O'Hare Airport",
      featured: false,
    },
    {
      code: "MKE",
      name: "Milwaukee Mitchell International Airport",
      distance: "120 miles south of Green Bay",
      address: "5300 S Howell Ave, Milwaukee, WI 53207",
      note: "Alternative international airport for Green Bay travelers. About 2-hour drive. More connections than GRB.",
      flightUrl: "https://www.mitchellairport.com/",
      flightLabel: "MKE Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from the Wisconsin Department of Health Services Vital Records office. Online, mail, or in-person options for Brown County and northeast Wisconsin.", url: "https://www.dhs.wisconsin.gov/vitalrecords/birth.htm" },
    { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through Wisconsin Vital Records. Required for estate and legal matters in Brown County.", url: "https://www.dhs.wisconsin.gov/vitalrecords/death.htm" },
    { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Brown County Register of Deeds at 305 E Walnut St. Certified copies for name changes and passport applications.", url: "https://www.browncountywi.gov/" },
    { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through the Brown County Clerk of Circuit Court. Required for name change passport corrections.", url: "https://www.wicourts.gov/courts/circuit/brown.htm" },
  ],
};